import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const CookiePolicy = () => {
  const lastUpdated = "November 22, 2025";
  
  const [cookiePreferences, setCookiePreferences] = useState(() => {
    try {
      const saved = localStorage.getItem('cookiePreferences');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Failed to parse cookie preferences', e);
    }
    return {
      essential: true,
      analytics: true,
      functional: true,
      advertising: false
    };
  });

  const [saveMessage, setSaveMessage] = useState('');

  const cookieCategories = [
    {
      id: 'essential',
      title: 'Essential Cookies',
      icon: 'Lock',
      description: 'These cookies are necessary for the website to function and cannot be switched off.',
      required: true,
      examples: [
        'Authentication and session management',
        'Security and fraud prevention',
        'Load balancing and performance',
        'User preference storage'
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      icon: 'BarChart3',
      description: 'Help us understand how visitors interact with our website by collecting and reporting information.',
      required: false,
      examples: [
        'Google Analytics for usage statistics',
        'Page view and interaction tracking',
        'Performance monitoring',
        'Error reporting and diagnostics'
      ]
    },
    {
      id: 'functional',
      title: 'Functional Cookies',
      icon: 'Settings',
      description: 'Enable enhanced functionality and personalization based on your preferences.',
      required: false,
      examples: [
        '3D model viewing preferences',
        'Language and region settings',
        'Display mode and layout choices',
        'Recently viewed items'
      ]
    },
    {
      id: 'advertising',
      title: 'Advertising Cookies',
      icon: 'Target',
      description: 'Used to deliver advertisements more relevant to you and your interests.',
      required: false,
      examples: [
        'Targeted advertising based on browsing',
        'Ad performance measurement',
        'Third-party advertising networks',
        'Remarketing and retargeting'
      ]
    }
  ];

  const handleToggle = (categoryId) => {
    if (categoryId === 'essential') return;
    
    setCookiePreferences(prev => ({
      ...prev,
      [categoryId]: !prev?.[categoryId]
    }));
  };

  const handleSavePreferences = () => {
    try {
      localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
      setSaveMessage('Your cookie preferences have been successfully saved.');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (e) {
      console.error('Failed to save cookie preferences', e);
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functional: true,
      advertising: true
    };
    setCookiePreferences(allAccepted);
    try {
      localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
      setSaveMessage('All cookies have been accepted and saved.');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (e) {
      console.error('Failed to save cookie preferences', e);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white">
        <div className="container mx-auto px-6 py-16">
          <Link
            to="/"
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} className="mr-2" />
            Back to Home
          </Link>
          
          <div className="max-w-4xl">
            <div className="flex items-center mb-4">
              <Icon name="Cookie" size={48} className="mr-4" />
              <h1 className="text-4xl lg:text-5xl font-bold">
                Cookie Policy
              </h1>
            </div>
            <p className="text-xl text-white/90 mb-6">
              Learn about how we use cookies and similar technologies to provide you with the best experience on OpenGadget.
            </p>
            <div className="flex items-center text-white/80">
              <Icon name="Calendar" size={16} className="mr-2" />
              <span className="text-sm">Last updated: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-start mb-6">
              <div className="bg-amber-100 rounded-xl p-3 mr-4">
                <Icon name="Info" size={24} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-3">
                  What Are Cookies?
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our platform.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  This Cookie Policy explains what cookies are, how we use them, the types of cookies we use, and how you can control them.
                </p>
              </div>
            </div>
          </div>

          {/* Cookie Preference Center */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">
                  Cookie Preference Center
                </h2>
                <p className="text-slate-600">
                  Manage your cookie preferences below. Changes take effect immediately.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSavePreferences}
                  iconName="Save"
                  iconPosition="left"
                >
                  Save Preferences
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAcceptAll}
                  iconName="Check"
                  iconPosition="left"
                >
                  Accept All
                </Button>
              </div>
            </div>

            {saveMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center transition-all">
                <Icon name="Check" size={20} className="mr-3 flex-shrink-0" />
                <span className="font-medium text-sm">{saveMessage}</span>
              </div>
            )}

            <div className="space-y-6">
              {cookieCategories?.map((category) => (
                <div
                  key={category?.id}
                  className="border border-slate-200 rounded-xl p-6 hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start flex-1">
                      <div className="bg-primary/10 rounded-lg p-2 mr-4">
                        <Icon name={category?.icon} size={24} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h3 className="text-lg font-semibold text-slate-800 mr-2">
                            {category?.title}
                          </h3>
                          {category?.required && (
                            <span className="bg-slate-100 text-slate-600 text-xs px-2 py-1 rounded">
                              Always Active
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 text-sm mb-4">
                          {category?.description}
                        </p>
                        <div className="bg-slate-50 rounded-lg p-4">
                          <p className="text-xs font-semibold text-slate-700 mb-2">
                            Examples:
                          </p>
                          <ul className="space-y-1">
                            {category?.examples?.map((example, index) => (
                              <li key={index} className="flex items-start text-xs text-slate-600">
                                <Icon name="Check" size={14} className="text-primary mr-2 mt-0.5 flex-shrink-0" />
                                {example}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleToggle(category?.id)}
                      disabled={category?.required}
                      className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        cookiePreferences?.[category?.id]
                          ? 'bg-primary' :'bg-slate-300'
                      } ${category?.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          cookiePreferences?.[category?.id]
                            ? 'translate-x-6' :'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cookie Types Details */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-start mb-6">
              <div className="bg-blue-100 rounded-xl p-3 mr-4">
                <Icon name="List" size={24} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-3">
                  Types of Cookies We Use
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-2">Session Cookies</h3>
                    <p className="text-slate-600 text-sm">
                      Temporary cookies that expire when you close your browser. Used for essential functionality like maintaining your login session.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-2">Persistent Cookies</h3>
                    <p className="text-slate-600 text-sm">
                      Remain on your device for a set period or until you delete them. Used to remember your preferences across visits.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-2">First-Party Cookies</h3>
                    <p className="text-slate-600 text-sm">
                      Set directly by OpenGadget to provide core functionality and improve user experience.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-2">Third-Party Cookies</h3>
                    <p className="text-slate-600 text-sm">
                      Set by external services we use (like Google Analytics) to analyze usage and provide additional features.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Managing Cookies */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-start mb-6">
              <div className="bg-purple-100 rounded-xl p-3 mr-4">
                <Icon name="Settings" size={24} className="text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-3">
                  How to Manage Cookies
                </h2>
                <p className="text-slate-600 mb-4">
                  You have several options for managing cookies on your device:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Icon name="Check" size={20} className="text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">
                      <strong>Use our Cookie Preference Center above</strong> to customize which cookies you accept
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={20} className="text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">
                      <strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies through their settings
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={20} className="text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">
                      <strong>Third-Party Tools:</strong> Use tools like browser extensions to manage tracking cookies
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={20} className="text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">
                      <strong>Opt-Out Links:</strong> Many analytics providers offer opt-out mechanisms on their websites
                    </span>
                  </li>
                </ul>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
                  <div className="flex items-start">
                    <Icon name="AlertCircle" size={20} className="text-amber-600 mr-3 mt-0.5" />
                    <p className="text-sm text-slate-700">
                      <strong>Note:</strong> Blocking or deleting cookies may impact your experience on OpenGadget. Some features may not work properly without essential cookies.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact and Resources */}
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="flex items-start">
              <Icon name="HelpCircle" size={32} className="mr-4 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold mb-4">
                  Questions About Cookies?
                </h2>
                <p className="text-white/90 mb-6">
                  If you have questions about our use of cookies or this Cookie Policy, please contact us.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button
                    variant="secondary"
                    iconName="Mail"
                    iconPosition="left"
                    onClick={() => window.location.href = '/support-center'}
                  >
                    Contact Support
                  </Button>
                  <Button
                    variant="secondary"
                    iconName="FileText"
                    iconPosition="left"
                    onClick={() => window.location.href = '/privacy-policy'}
                  >
                    Privacy Policy
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;